import axios from '@/lib/axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { get } from '@vueuse/core'
import { type MaybeRef, computed } from 'vue'
import { useToast } from 'vue-toastification'

import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import type {
	CompanyEmailSetting,
	CompanyEmailSettingUpdateBody,
	EmailType,
	SendTestBody,
} from '@/modules/companies/interfaces/emailSettings'

/**
 * `errorHandling: false` en las tres llamadas es obligatorio, no cosmético: el
 * interceptor global de `@/lib/axios` manda los 403 a /error/forbidden y los 404 a
 * /error/not-found, o sea que sacaría al usuario de "Mi empresa" en lugar de dejar
 * que la pestaña muestre el candado del plan o el estado de sólo lectura.
 */
const fetchSettings = async (
	companyId: number | string
): Promise<CompanyEmailSetting[]> => {
	const { data } = await axios.get<CompanyEmailSetting[]>(
		`/api/companies/${companyId}/email-settings`,
		{ errorHandling: false } as CustomAxiosRequestConfig
	)
	return data
}

const updateSetting = async (
	companyId: number | string,
	body: CompanyEmailSettingUpdateBody
): Promise<CompanyEmailSetting> => {
	const { data } = await axios.put<CompanyEmailSetting>(
		`/api/companies/${companyId}/email-settings`,
		body,
		{ errorHandling: false } as CustomAxiosRequestConfig
	)
	return data
}

const sendTest = async (
	companyId: number | string,
	body: SendTestBody
): Promise<{ message: string }> => {
	const { data } = await axios.post<{ message: string }>(
		`/api/companies/${companyId}/email-settings/test`,
		body,
		{ errorHandling: false } as CustomAxiosRequestConfig
	)
	return data
}

const messageFrom = (err: unknown): string | undefined =>
	(err as { response?: { data?: { message?: string } } })?.response?.data
		?.message

const useCompanyEmailSettings = (
	companyId: MaybeRef<number | string | undefined>
) => {
	const toast = useToast()
	const queryClient = useQueryClient()

	const resolvedId = computed(() => get(companyId))
	const queryKey = computed(() => ['companyEmailSettings', resolvedId.value])

	const { data, isLoading, isError, error } = useQuery({
		queryKey,
		queryFn: () => fetchSettings(resolvedId.value!),
		enabled: computed(() => !!resolvedId.value),
		retry: false,
		refetchOnWindowFocus: false,
	})

	const settings = computed(() => data.value ?? [])

	/**
	 * Una feature pausada responde 422 con `reason: 'paused'`.
	 *
	 * Hay que distinguirlo de un error de verdad: es un estado esperado —el módulo está
	 * congelado a propósito— y pintarlo en rojo como "no se pudo cargar" hace que el
	 * cliente crea que algo se rompió y lo reporte. También hay que distinguirlo del
	 * gateo por plan, porque ahí sí toca ofrecer el upgrade y aquí sería mentira.
	 */
	const pausedResponse = computed(() => {
		const data = (
			error.value as {
				response?: { data?: { reason?: string; message?: string } }
			}
		)?.response?.data

		return data?.reason === 'paused' ? (data.message ?? '') : null
	})

	const isPaused = computed(() => pausedResponse.value !== null)

	const settingFor = (type: EmailType) =>
		settings.value.find((s) => s.emailType === type) ?? null

	const saveMutation = useMutation(
		(body: CompanyEmailSettingUpdateBody) =>
			updateSetting(resolvedId.value!, body),
		{
			onSuccess: () => {
				toast.success('Configuración de correos guardada')
				queryClient.invalidateQueries({ queryKey: queryKey.value })
			},
			onError: (err: unknown) => {
				toast.error(messageFrom(err) ?? 'No se pudo guardar la configuración')
			},
		}
	)

	const testMutation = useMutation(
		(body: SendTestBody) => sendTest(resolvedId.value!, body),
		{
			onSuccess: (result) => {
				toast.success(result.message ?? 'Enviamos el correo de prueba')
			},
			onError: (err: unknown) => {
				toast.error(messageFrom(err) ?? 'No se pudo enviar la prueba')
			},
		}
	)

	return {
		settings,
		settingFor,
		isLoading,
		isError,
		error,
		isPaused,
		pausedNote: pausedResponse,
		save: saveMutation.mutate,
		isSaving: saveMutation.isLoading,
		savingEmailType: computed(
			() => saveMutation.variables.value?.emailType ?? null
		),
		sendTest: testMutation.mutate,
		isSendingTest: testMutation.isLoading,
	}
}

export default useCompanyEmailSettings
